/**
 * Learn asset resolver. Art is hosted (Supabase Storage CDN), never bundled or
 * git-tracked. Keys are the relative path without extension, e.g.
 *   "letters/a/a_word_apple", "counting/activities/counting_piece_duck",
 *   "alphabet_world_garden", "ui/badge_letters".
 * The manifest (generated by scripts/learn-assets/build_learn_assets.py) gives
 * intrinsic sizes, whether a @2x exists and whether a blur placeholder exists.
 */
import manifestJson from "../../../data/learnAssetManifest.json";

const manifest = manifestJson || { base: "", assets: {} };

export const LEARN_ASSET_BASE =
  manifest.base ||
  "https://huzyagtfxwapsccsksib.supabase.co/storage/v1/object/public/videos/assets/learn/v1";

export function normalizeAssetKey(key) {
  if (!key) return "";
  return String(key)
    .replace(/^\/?assets\/learn\//, "")
    .replace(/^\//, "")
    .replace(/\.(png|jpg|jpeg|webp)$/i, "");
}

/** Duplicate paths the pipeline collapsed (e.g. writing/a_word_apple → letters/a/a_word_apple). */
function canonicalKey(key) {
  const k = normalizeAssetKey(key);
  return manifest.aliases?.[k] || k;
}

export function learnAssetMeta(key) {
  return manifest.assets?.[canonicalKey(key)] || null;
}

/** 1x URL (the smaller rendition). */
export function learnAssetUrl(key, { size = "1x" } = {}) {
  if (/^https?:\/\//.test(String(key)) || String(key).startsWith("/assets/")) {
    return String(key); // already absolute (legacy / non-learn assets)
  }
  const k = canonicalKey(key);
  if (!k) return "";
  const meta = learnAssetMeta(k);
  if (size === "2x" && meta?.x2) return `${LEARN_ASSET_BASE}/${k}@2x.webp`;
  if (size === "blur" && meta?.blur) return `${LEARN_ASSET_BASE}/${k}.blur.webp`;
  return `${LEARN_ASSET_BASE}/${k}.webp`;
}

export function learnAssetSrcSet(key) {
  const meta = learnAssetMeta(key);
  const one = learnAssetUrl(key);
  if (!meta?.x2) return one;
  return `${one} 1x, ${learnAssetUrl(key, { size: "2x" })} 2x`;
}

/** Warm the browser cache for a list of keys (e.g. next lesson's art). */
export function prefetchLearnAssets(keys = []) {
  if (typeof window === "undefined") return;
  keys.forEach((k) => {
    const url = learnAssetUrl(k);
    if (!url) return;
    const img = new Image();
    img.decoding = "async";
    img.src = url;
  });
}

// ── Legacy helper shims (engines call these with bare names) ─────────────
export const LEARN_ASSET = (name) => learnAssetUrl(name);

export function letterAsset(letter, name) {
  if (!name) return null;
  if (/^https?:\/\//.test(name) || name.startsWith("/")) return name;
  const L = String(letter || "a").toLowerCase();
  return learnAssetUrl(`letters/${L}/${normalizeAssetKey(name)}`);
}

/**
 * Game backgrounds live under the letter they were painted for; several
 * configs borrow another letter's scene (H uses i_game_…), so resolve by the
 * file's own prefix letter, falling back to the current letter.
 */
export function letterBackgroundKey(letter, background) {
  if (!background) return null;
  const name = normalizeAssetKey(background);
  const m = name.match(/^([a-z])_game_/);
  const L = m ? m[1] : String(letter || "a").toLowerCase();
  return `letters/${L}/${name}`;
}

export const COUNTING_ASSET = (rel) => learnAssetUrl(`counting/${normalizeAssetKey(rel)}`);
export const COUNTING_PIECE = (name) => COUNTING_ASSET(`activities/counting_piece_${name}`);

export const WRITING_ASSET = (name) => (name ? learnAssetUrl(`writing/${normalizeAssetKey(name)}`) : "");
export const WRITING_SHARED_ASSET = (name) => (name ? learnAssetUrl(normalizeAssetKey(name)) : "");
export const WRITING_WORD_ASSET = (name) => {
  if (!name) return "";
  const k = normalizeAssetKey(name);
  const m = k.match(/^([a-z])_word_/);
  return m ? learnAssetUrl(`letters/${m[1]}/${k}`) : learnAssetUrl(`writing/${k}`);
};
/** Generated Pip-style UI art (badges, stickers, companion poses). */
export const LEARN_UI = (slug) => learnAssetUrl(`ui/${slug}`);
